import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { superadminApi, Superadmin } from '../lib/superadminApi';
import { initializeSession } from '../lib/routeEncryption';

interface SuperadminAuthContextType {
    superadmin: Superadmin | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    login: (email: string, password: string) => Promise<{ success: boolean; message: string }>;
    logout: () => void;
    checkAuth: () => Promise<void>;
}

const SuperadminAuthContext = createContext<SuperadminAuthContextType | undefined>(undefined);

export const useSuperadminAuth = () => {
    const context = useContext(SuperadminAuthContext);
    if (context === undefined) {
        throw new Error('useSuperadminAuth debe ser usado dentro de un SuperadminAuthProvider');
    }
    return context;
};

interface SuperadminAuthProviderProps {
    children: ReactNode;
}

export const SuperadminAuthProvider: React.FC<SuperadminAuthProviderProps> = ({ children }) => {
    const [superadmin, setSuperadmin] = useState<Superadmin | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    const isAuthenticated = !!superadmin;

    
    // Guardar estado en localStorage y cookies
    const saveToStorage = (superadminData: Superadmin | null, token?: string) => {
                        
        try {
            if (superadminData && token) {
                const dataToStore = {
                    superadmin: superadminData,
                    token: token,
                    isAuthenticated: true,
                    timestamp: new Date().toISOString()
                };
                                localStorage.setItem('superadmin-auth', JSON.stringify(dataToStore));

                // También guardar en cookies para que el backend pueda leerlo
                                const expires = new Date();
                expires.setTime(expires.getTime() + (24 * 60 * 60 * 1000)); // 24 horas

                // Para localhost, no especificar dominio (el navegador lo maneja automáticamente)
                const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
                const secure = process.env.NODE_ENV === 'production';

                let cookieString;
                if (isLocalhost) {
                    // Para localhost, omitir dominio y usar SameSite=Lax
                    cookieString = `superadmin_token=${token}; expires=${expires.toUTCString()}; path=/; SameSite=Lax;`;
                } else {
                    // Para producción, NO especificar dominio para que el navegador lo determine automáticamente
                    // Esto permite que la cookie funcione con app.compilatime.es, www.app.compilatime.es, etc.
                    // SameSite=Lax para permitir peticiones AJAX a través de Nginx
                    cookieString = `superadmin_token=${token}; expires=${expires.toUTCString()}; path=/; SameSite=Lax;${secure ? ' Secure;' : ''}`;
                }

                                                
                document.cookie = cookieString;

                // Pequeña espera para que el navegador procese la cookie
                setTimeout(() => {
                                                            const cookies = document.cookie.split(';');
                    const superadminTokenCookie = cookies.find(cookie =>
                        cookie.trim().startsWith('superadmin_token=')
                    );
                    if (superadminTokenCookie) {
                                            } else {
                                            }
                }, 100);
            } else {
                                localStorage.removeItem('superadmin-auth');
                // Eliminar cookie con los mismos parámetros
                const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
                if (isLocalhost) {
                    document.cookie = `superadmin_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
                } else {
                    document.cookie = `superadmin_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${window.location.hostname};`;
                }
            }
        } catch (error) {
            console.error('❌ [saveToStorage] Error guardando en storage:', error);
        }
    };

    // Cargar estado desde localStorage
    const loadFromStorage = () => {
        try {
            const stored = localStorage.getItem('superadmin-auth');
                        if (stored) {
                const parsedData = JSON.parse(stored);
                                if (parsedData.isAuthenticated && parsedData.superadmin) {
                                        setSuperadmin(parsedData.superadmin);
                }
            }
        } catch (error) {
            console.error('Error cargando desde localStorage:', error);
        }
    };

    // Verificar autenticación al cargar
    const checkAuth = async () => {
        try {
                        setIsLoading(true);
            const response = await superadminApi.getMe();
            
            if (response.success && response.data) {
                                setSuperadmin(response.data);
                saveToStorage(response.data);
            } else {
                                setSuperadmin(null);
                saveToStorage(null);
            }
        } catch (error) {
            console.error('Error verificando autenticación de superadmin:', error);
            setSuperadmin(null);
            saveToStorage(null);
        } finally {
            setIsLoading(false);
        }
    };

    // Login
    const login = async (email: string, password: string): Promise<{ success: boolean; message: string }> => {
        try {
                        const response = await superadminApi.login({ email, password });
            
            if (response.success && response.data) {
                                console.log('🔍 [login] Token recibido (primeros 50 chars):', response.data.token.substring(0, 50) + '...');
                
                // Inicializar sesión para SecureRoute
                initializeSession();
                console.log('SuperadminAuthContext: Sesión inicializada con initializeSession()');

                setSuperadmin(response.data.superadmin);
                saveToStorage(response.data.superadmin, response.data.token);
                return { success: true, message: response.message || 'Login exitoso' };
            } else {
                                return { success: false, message: response.message || 'Error en el login' };
            }
        } catch (error) {
            console.error('Error en login de superadmin:', error);
            return { success: false, message: 'Error de conexión' };
        }
    };

    // Logout
    const logout = async () => {
        try {
                        await superadminApi.logout();
        } catch (error) {
            console.error('Error en logout de superadmin:', error);
        } finally {
                        setSuperadmin(null);
            saveToStorage(null);
            // Eliminar cookie manualmente con los mismos parámetros
            const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
            if (isLocalhost) {
                document.cookie = `superadmin_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
            } else {
                document.cookie = `superadmin_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${window.location.hostname};`;
            }
        }
    };

    useEffect(() => {
        // Cargar estado desde localStorage al iniciar
        loadFromStorage();

        // Verificar autenticación si estamos en una ruta protegida de admin
        if (typeof window !== 'undefined' && window.location.pathname.startsWith('/admin/') &&
            !window.location.pathname.includes('/admin/login')) {
                        checkAuth();
        } else {
                        setIsLoading(false);
        }
    }, []);

    const value: SuperadminAuthContextType = {
        superadmin,
        isAuthenticated,
        isLoading,
        login,
        logout,
        checkAuth
    };

    

        return (
        <SuperadminAuthContext.Provider value={value}>
            {children}
        </SuperadminAuthContext.Provider>
    );
};
